import { FC, useCallback, useEffect, useState, type JSX } from 'react';

import BasicField from '@payaca/components/basicField/BasicField';
import FieldLabel from '@payaca/components/fieldLabel/FieldLabel';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import TypeToSearchField from '@payaca/components/typeToSearchField/TypeToSearchField';
import {
  requestGetCustomer,
  requestGetListedCustomersPage,
} from '@payaca/store/customer/customerActions';
import { useCustomer, useSelector } from '@payaca/store/hooks/appState';
import { ListedCustomer } from '@payaca/types/listedCustomerTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useDispatch } from 'react-redux';
import './CustomerFilterControl.sass';

type Props = {
  selectedCustomerId?: number;
  onChange: (selectedCustomerId?: number) => void;
  styleVariant?: InputStyleVariant;
  displayValue?: string;
};
const CustomerFilterControl: FC<Props> = ({
  selectedCustomerId,
  onChange,
  styleVariant,
  displayValue,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [renderField, setRenderField] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const [requiresGetListedCustomersPage, setRequiresGetListedCustomersPage] =
    useState(false);

  const customer = useCustomer(selectedCustomerId);

  const isGettingListedCustomersPage = useSelector(
    (state) => state.customer.isGettingListedCustomersPage
  );

  const listedCustomers: ListedCustomer[] = useSelector((state) => {
    return state.customer?.listedCustomersPage?.items || [];
  });

  const requestGetListedCustomers = useCallback(() => {
    dispatch(
      requestGetListedCustomersPage({
        pageSize: 20,
        pageNumber: 1,
        searchTerm: searchTerm,
      })
    );
    setRequiresGetListedCustomersPage(false);
  }, [searchTerm, dispatch]);

  useEffect(() => {
    if (requiresGetListedCustomersPage) {
      requestGetListedCustomers();
    }
  }, [requiresGetListedCustomersPage, requestGetListedCustomers]);

  useEffect(() => {
    if (selectedCustomerId) {
      dispatch(requestGetCustomer(selectedCustomerId));
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [selectedCustomerId]);

  useEffect(() => {
    if (renderField) {
      setRequiresGetListedCustomersPage(true);
    }
  }, [renderField]);

  return (
    <div className="customer-filter-control">
      <FieldLabel label="Customer" />
      {!renderField && (
        <BasicField
          styleVariant={styleVariant}
          name=""
          value={displayValue || customer?.name}
          additionalInputProps={{
            onFocus: () => setRenderField(true),
          }}
          placeholder="Search Customers"
          iconAfter={
            selectedCustomerId ? (
              <UntitledIcon
                name="x.3"
                onClick={() => {
                  onChange();
                }}
              />
            ) : (
              <UntitledIcon
                name="chevron-down"
                onClick={() => setRenderField(true)}
              />
            )
          }
        />
      )}
      {renderField && (
        <TypeToSearchField
          styleVariant={styleVariant}
          autofocus={true}
          onBlur={() => setRenderField(false)}
          placeholder="Search Customers"
          options={listedCustomers}
          disableFilteringInComponent={true}
          disableCloseOnSelect={false}
          openOnFocus={true}
          onSearchTermChangeTimeout={() =>
            setRequiresGetListedCustomersPage(true)
          }
          isLoadingOptions={isGettingListedCustomersPage}
          onSearchTermChange={setSearchTerm}
          onSelectOption={(customer) => {
            onChange(customer?.customerId);
          }}
          renderOption={(customer, props) => {
            return (
              <li {...props}>
                <span>{customer.customerName}</span>
              </li>
            );
          }}
          getOptionLabel={(customer) => customer.customerName ?? ''}
          blockInputChangeReasons={['reset']}
          multiple={false}
        />
      )}
    </div>
  );
};

export default CustomerFilterControl;
